import { Box, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import {
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useMuiTableToolbarAlertBannerProps } from "@/hooks/use-mui-table-toolbar-alert-banner-props";
import { useTableSettings } from "@/hooks/use-table-settings";
import { HandHistoryErrorItemDto } from "@/shared/api";

import { useQueryHandHistoryErrors } from "./hooks/use-query-hand-history-errors";

const HhErrorsJournalModule = () => {
  const { t } = useTranslation();

  const columns = useMemo<MRT_ColumnDef<HandHistoryErrorItemDto>[]>(
    () => [
      {
        id: "timestamp_ms",
        header: t("hhErrorsTable.timestamp"),
        accessorFn: (row) =>
          row.timestamp_ms ? dayjs.utc(row.timestamp_ms).format("YYYY-MM-DD HH:mm:ss") : "-",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        id: "room_id",
        header: t("hhErrorsTable.roomId"),
        accessorKey: "room_id",
      },
      {
        id: "game_format",
        header: t("hhErrorsTable.gameFormat"),
        accessorKey: "game_format",
      },
      {
        id: "game_type",
        header: t("hhErrorsTable.gameType"),
        accessorKey: "game_type",
      },
      {
        id: "game_id",
        header: t("hhErrorsTable.gameId"),
        accessorKey: "game_id",
      },
      {
        id: "trace_id",
        header: t("hhErrorsTable.traceId"),
        accessorKey: "trace_id",
      },
      {
        id: "error_message",
        header: t("hhErrorsTable.errorMessage"),
        accessorKey: "error_message",
        Cell: ({ cell }) => {
          const value = String(cell.getValue() ?? "");
          return (
            <Tooltip title={value}>
              <Box
                sx={{
                  maxWidth: "400px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {value}
              </Box>
            </Tooltip>
          );
        },
      },
    ],
    [t]
  );

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  });
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<MRT_SortingState>([
    { id: "timestamp_ms", desc: true },
  ]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [columnFilters]);

  const orderAsc = useMemo(
    () => !(sorting.find((it) => it.id === "timestamp_ms")?.desc ?? true),
    [sorting]
  );

  const {
    data: response,
    isError,
    isLoading,
    isRefetching,
    error,
  } = useQueryHandHistoryErrors({
    pageNumber: pagination.pageIndex,
    pageSize: pagination.pageSize,
    columnFilters,
    orderAsc,
  });

  const { defaultProps } = useTableSettings<HandHistoryErrorItemDto>("hhErrorsJournal");

  const muiToolbarAlertBannerProps = useMuiTableToolbarAlertBannerProps(
    Boolean(isError),
    error
  );

  const table = useMaterialReactTable({
    data: response?.data.items ?? [],
    columns,
    ...defaultProps,
    initialState: {
      density: "compact",
    },
    muiToolbarAlertBannerProps,
    state: {
      pagination,
      columnFilters,
      sorting,
      isLoading,
      showProgressBars: isRefetching,
    },
    rowCount: response?.data.pagination.items_total,
    pageCount: response?.data.pagination.pages_total,
    enableRowSelection: false,
    enableGrouping: false,
    enableColumnDragging: false,
    enableColumnFilterModes: false,
    enableHiding: false,
    enableColumnFilters: true,
    enableDensityToggle: false,
    enableGlobalFilter: false,
    enablePagination: true,
    enableStickyHeader: true,
    enableSorting: true,
    enableSortingRemoval: false,
    enableMultiSort: false,
    autoResetPageIndex: false,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    renderDetailPanel: ({ row }) =>
      row.original.data ? (
        <Box
          component="pre"
          sx={{ margin: 0, maxWidth: "100%", whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {row.original.data}
        </Box>
      ) : null,
  });

  return <MaterialReactTable table={table} />;
};

export default HhErrorsJournalModule;
